import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { QR_LIST_REVIEW } from "../graphQl/ReviewAPI";

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const avg = (arr) => {
  if (!arr?.length) return 0;
  const m = arr.reduce((a, b) => a + b, 0) / arr.length;
  return Math.round(m * 10) / 10;
};

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? clamp(n, 0, 5) : 0;
};

export const useReviewList = (tripPublicationId, paginationParams, options = {}) => {
  const { enabled = true } = options;

  const { data, loading, error: apolloError, refetch } = useQuery(QR_LIST_REVIEW, {
    variables: { tripPublicationId, paginationParams },
    skip: !enabled || !tripPublicationId,
    fetchPolicy: "network-only",
  });

  const response = data?.QR_LIST_REVIEW;
  const content = response?.data?.content;

  // ✅ NEW: priorité à reviews (enrichi), sinon opinions (legacy)
  const reviews = useMemo(() => {
    const listReviews = content?.reviews ?? [];
    if (Array.isArray(listReviews) && listReviews.length) return listReviews;

    const listOpinions = content?.opinions ?? [];
    return Array.isArray(listOpinions) ? listOpinions : [];
  }, [content]);

  // ✅ NEW: support both field names (qualityServiceMark vs serviceQualityMark)
  const serviceAgg = safeNum(
    content?.qualityServiceMark ?? content?.serviceQualityMark
  );

  // ✅ Airbnb criteria: agrégé si dispo, sinon moyenne depuis reviews/opinions
  const criteria = useMemo(() => {
    const cAgg = safeNum(content?.complianceMark);
    const pqAgg = safeNum(content?.priceQualityMark);

    const compliance =
      cAgg > 0
        ? cAgg
        : avg(reviews.map((r) => safeNum(r?.complianceMark)).filter((x) => x > 0));

    const priceQuality =
      pqAgg > 0
        ? pqAgg
        : avg(reviews.map((r) => safeNum(r?.priceQualityMark)).filter((x) => x > 0));

    const serviceQuality =
      serviceAgg > 0
        ? serviceAgg
        : avg(
            reviews
              .map((r) =>
                safeNum(r?.qualityServiceMark ?? r?.serviceQualityMark)
              )
              .filter((x) => x > 0)
          );

    return [
      { key: "compliance", label: "Conformité", value: compliance },
      { key: "service", label: "Qualité de service", value: serviceQuality },
      { key: "priceQuality", label: "Rapport qualité/prix", value: priceQuality },
    ].filter((x) => x.value > 0);
  }, [content, reviews, serviceAgg]);

  return {
    loading,
    error: apolloError || response?.error || null,
    code: response?.code,

    // ✅ NEW: on renvoie la liste choisie
    reviews,
    globalMark: content?.globalMark ?? 0,
    criteria,

    totalPages: response?.data?.totalPages ?? 0,
    totalElements: response?.data?.totalElements ?? 0,
    currentPage: response?.data?.currentPage ?? 0,

    refetch,
  };
};
